import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { NAV, LINKS, IMAGES } from "@/data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E5DCC5]/60 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="h-11 w-11 rounded-full overflow-hidden ring-2 ring-[#E5DCC5] bg-white shrink-0 transition-transform group-hover:scale-105">
            <img
              src={IMAGES.logo}
              alt="Haven For Tails"
              className="h-full w-full object-cover scale-[1.18]"
            />
          </span>
          <span className="font-display text-lg font-bold text-[#2B2D42] leading-none hidden sm:block">
            Haven For Tails
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-[#4A4E69] hover:text-[#E07A5F] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#E07A5F] after:transition-all hover:after:w-full"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={LINKS.gofundme}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-donate-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(224,122,95,0.3)] transition-all hover:bg-[#D06A4F] active:scale-95"
          >
            <Heart className="h-4 w-4 fill-white" /> Donate
          </a>
          <button
            className="lg:hidden text-[#2B2D42]"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden mt-3 mx-6 rounded-2xl bg-white border border-[#E5DCC5] p-4 shadow-lg"
        >
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="block py-2.5 text-[#4A4E69] font-medium hover:text-[#E07A5F]"
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {item.label}
            </a>
          ))}
          <a
            href={LINKS.gofundme}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 block text-center rounded-full bg-[#E07A5F] px-5 py-2.5 font-semibold text-white"
          >
            Donate
          </a>
        </motion.div>
      )}
    </motion.header>
  );
};
